// Library imports
import { format } from 'date-fns';
import en from 'date-fns/locale/en-US';
import styled from 'styled-components';
import { Tag, Tooltip, Typography } from 'antd';
import store, { RootState } from 'store';
import { useSelector } from 'react-redux';
import RSelect, { components } from 'react-select';
import 'react-datepicker/dist/react-datepicker.css';
import DatePicker, { registerLocale } from 'react-datepicker';
import React, { ReactNode, useState, useEffect, useRef } from 'react';
// Local imports
import { ASSET_TYPE } from '../../../constants/index';
import Checkbox from 'modules/Common/Components/Checkbox';
import { ACCOUNTS_EMAIL_ENGAGEMENT_ANALYTICS, ANALYTICS_DASHBOARD_GRAPH_DATA, CONTACTS_EMAIL_ENGAGEMENT_ANALYTICS } from 'constants/types';
import { startDragAction } from 'modules/Content/DragnDrop/actions';
import { AUTOMATION_EMAILS_GRAPH_DATA } from 'constants/types/automationTypes';
// Icon imports
import { ReactComponent as Tick } from '../../../assets/img/icons/tick.svg';
import { ReactComponent as DownAngle } from '../../../assets/img/icons/DownAngle.svg';

registerLocale('en', en);
const START_DATE_INPUT_ID = 'date-picker-start';
const END_DATE_INPUT_ID = 'date-picker-end';
const { Paragraph } = Typography;
const ellipsis = true;

type optionObject = {
  value: string | number;
  label: string | number;
};
interface ISelectWithIcon {
  children?: ReactNode;
  options?: optionObject[];
  emoji?: ReactNode;
  defaultValue?: optionObject;
  value?: optionObject;
  heightWidth?: number;
  menuWidth?: number;
  className?: string;
  onChange?: (value: any) => void;
  customDatePicker?: ReactNode;
  isAnalytics?: boolean;
  isWorkflowGraph?: boolean;
  isSmartSource?: boolean;
  dateFilter?: any;
  setDateFilter?: (value: any) => void;
  datesObj?: any;
  setDatesObj?: (value: any) => void;
  filteredCampaigns?: (key: string, value: any, dates?: {}) => void;
  filterEmails?: (key: string, value: any, dates?: {}) => void;
  isCheckbox?: boolean;
  disabled: boolean;
  id: string;
  isAccountEmailEngagement?: boolean;
  isContactEmailEngagement?: boolean;
}

// Mini components
const DropdownIndicator = (props: any) => {
  return (
    <components.DropdownIndicator {...props}>
      <DownAngle />
    </components.DropdownIndicator>
  );
};

const Control = ({ children, ...props }: any) => {
  const { emoji } = props.selectProps;
  return (
    <components.Control {...props} className="qa-control">
      <EmojiWrapper>{emoji}</EmojiWrapper>
      <div style={{ display: 'contents' }} data-id="dropdwon-selected-wrapper">
        {children}
      </div>
    </components.Control>
  );
};

const SingleValue = ({ children, ...props }: any) => <components.SingleValue {...props}>{children}</components.SingleValue>;

const ValueContainer = ({ children, ...props }: any) => (
  <components.ValueContainer {...props}>
    <Tooltip
      placement="top"
      title={
        props.selectProps.id === 'date-select-filter-wrapper-date' || props.selectProps.id === 'date-select-filter-wrapper-user'
          ? props.selectProps.value.label
          : ''
      }
    >
      {children}
    </Tooltip>
  </components.ValueContainer>
);

const Option = ({ children, ...props }: any) => {
  return (
    <components.Option
      {...props}
      isDisabled={props.selectProps.customOption && props.value === ASSET_TYPE.CUSTOM}
      className={props.data.active !== undefined ? (!props.data.active ? 'deactivate-span' : '') : ''}
    >
      <span style={{ verticalAlign: 'middle' }} data-id={`option-${props.data.value}`}>
        <div className="checked-wrap w-100">
          {props.selectProps.isCheckbox ? (
            <Checkbox
              checked={props.isSelected}
              onChange={() => {
                props.setValue(props.data);
              }}
            />
          ) : props.selectProps.customOption && props.data.value === ASSET_TYPE.CUSTOM ? null : (
            <Tick style={{ marginRight: '.75rem', opacity: '0' }} className={`${props.isSelected && 'opacity-1'}`} />
          )}
          {props.selectProps.customOption && props.value === ASSET_TYPE.CUSTOM ? (
            props.selectProps.customOption
          ) : props.data.active !== undefined ? (
            !props.data.active ? (
              <Paragraph className={ellipsis ? 'text-truncate' : ''} ellipsis={{ rows: 1, tooltip: props.data.label }}>
                {props.data.label}
              </Paragraph>
            ) : (
              props.data.label
            )
          ) : (
            props.data.label
          )}
        </div>
        {props.data.active !== undefined ? !props.data.active ? <Tag className="deactivated-tag">Deactivated</Tag> : '' : null}
      </span>
    </components.Option>
  );
};

// Main Component
const SelectWithIcon: React.FC<ISelectWithIcon> = (props) => {
  // Props
  const { setDatesObj, setDateFilter, filteredCampaigns, filterEmails, datesObj, dateFilter, id } = props;

  // Redux States
  const { appliedStartDate, appliedEndDate } = useSelector((state: RootState) => state.analyticsGraph);

  // Local States
  const [endDate, setEndDate] = useState('');
  const [onFocus, setOnFocus] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [menuHeight, setMenuHeight] = useState(false); // increase the height of menu list
  const [showMenuIsOpen, setShowMenuIsOpen] = useState(false); // if the dates menu is open, we need to keep it open until the start/end date is selected
  const [maxStartDate, setMaxStartDate] = useState(new Date());
  const [showCalendarEndDate, setShowCalendarEndDate] = useState(false); // show/hide end calendar
  const [showCalendarStartDate, setShowCalendarStartDate] = useState(false); // show/hide start calendar

  // useRefs
  const refStartDate: any = useRef(null); // reference for the start date calendar component container

  // Local variables
  let select: any = null; // reference for select component
  const styles = {
    option: (provided: any, state: any) => ({
      ...provided,
      fontFamily: `"Roboto", sans-serif`,
      color: state.isSelected ? '#27CE0A' : '#67696A',
      backgroundColor: state.isSelected ? '#ffffff' : '#ffffff',
      position: 'relative',
      display: 'block',
      minHeight: '28px',
      padding: '4px 12px',
      fontWeight: 500,
      fontSize: '16px',
      cursor: 'pointer',
      transition: 'background 0.3s ease',
      marginTop: '2px',
      overflowX: 'none',
      verticalAlign: 'middle',
      '&:active': {
        backgroundColor: '#FFFFFF !important',
      },
      '&:hover': {
        backgroundColor: '#f5f5f5 !important',
      },
      '@media all and (max-height:768px)': {
        fontSize: '12px',
        verticalAlign: 'middle',
      },
    }),
    control: (styles: any, state: any) => {
      let width = 0;
      if (state.getValue()[0]['label'] === undefined) width = 8 * 'custom'.length + 85;
      else width = 8 * state.getValue()[0]['label'].length + 85;
      return {
        ...styles,
        height: '38px',
        color: '#7E7E7E',
        textOverflow: 'inherit',
        width: width + 'px',
        backgroundColor: 'white',
        borderRadius: '7px',
        paddingLeft: '10px',
        paddingRight: '2px',
        cursor: 'pointer',
        boxShadow: state.isFocused ? '0 0 0 0.1rem rgb(40 167 69 / 50%)' : 'none',
        borderColor: state.isFocused || state.isSelected ? 'rgb(40 167 69 / 50%) !important' : 'hsl(0, 0%, 80%)',
        minWidth: '100%',
        flex: 1,
      };
    },
    valueContainer: (styles: any) => ({ ...styles }),
    singleValue: (provided: any, state: any) => {
      const opacity = state.isDisabled ? 0.5 : 1;
      const transition = 'opacity 300ms';
      const style = {
        fontFamily: `robotomedium`,
        color: '#7e7e7e',
        fontSize: '16px',
        width: '100%',
      };

      return { opacity, transition, ...provided, ...style };
    },
    input: (styles: any) => ({ ...styles, border: '1px solid red' }),
    menu: (styles: any) => ({ ...styles, width: 'max-content', minWidth: '100%' }),
    menuList: (base: any) => ({
      ...base,
      ...(props.menuWidth ? { width: props.menuWidth + 'px' } : { width: '300px' }),
      minWidth: '100%',
      ...(menuHeight ? { maxHeight: '700px', height: 'auto' } : {}),
      '@media all and (max-height:768px)': {
        ...(menuHeight ? { height: 'auto' } : {}),
      },
      '::-webkit-scrollbar': {
        width: '0px',
      },
      '::-webkit-scrollbar-track': {
        background: 'grey',
      },
      '::-webkit-scrollbar-thumb': {
        background: '#888',
      },
      '::-webkit-scrollbar-thumb:hover': {
        background: '#555',
      },
    }),
  };

  // method for set the redux value and dates when custom dates
  const setDates = (start_date: string, end_date: string) => {
    if (props.isAnalytics) {
      store.dispatch({
        type: ANALYTICS_DASHBOARD_GRAPH_DATA.SET_DATES_OBJECT,
        payload: { label: format(new Date(start_date), 'MMM dd, yy') + ' to ' + format(new Date(end_date), 'MMM dd, yy'), value: 'custom' },
      });
      store.dispatch({
        type: ANALYTICS_DASHBOARD_GRAPH_DATA.SET_DATES,
        ...{ start_date, end_date },
      });
    }
    if (props.isWorkflowGraph) {
      store.dispatch({
        type: AUTOMATION_EMAILS_GRAPH_DATA.SET_DATE,
        payload: {
          selectedDateFilter: {
            label: format(new Date(start_date), 'MMM dd, yy') + ' to ' + format(new Date(end_date), 'MMM dd, yy'),
            value: 'custom',
          },
          selectedDateValue: { start_date, end_date },
        },
      });
    }
    if (props?.isAccountEmailEngagement) {
      store.dispatch({
        type: ACCOUNTS_EMAIL_ENGAGEMENT_ANALYTICS.SET_DATES_OBJECT,
        payload: { label: format(new Date(start_date), 'MMM dd, yy') + ' to ' + format(new Date(end_date), 'MMM dd, yy'), value: 'custom' },
      });
      store.dispatch({
        type: ACCOUNTS_EMAIL_ENGAGEMENT_ANALYTICS.SET_DATES,
        ...{ start_date, end_date },
      });
    }
    if (props?.isContactEmailEngagement) {
      store.dispatch({
        type: CONTACTS_EMAIL_ENGAGEMENT_ANALYTICS.SET_DATES_OBJECT,
        payload: { label: format(new Date(start_date), 'MMM dd, yy') + ' to ' + format(new Date(end_date), 'MMM dd, yy'), value: 'custom' },
      });
      store.dispatch({
        type: CONTACTS_EMAIL_ENGAGEMENT_ANALYTICS.SET_DATES,
        ...{ start_date, end_date },
      });
    }
    select.blur();
    setShowMenuIsOpen(false);
  };

  // custom option for rending fields for custom dates in date filters dropdown
  const CustomOption = ({ isActive }: any) => (
    <>
      <div className="date-pickers">
        <Tick style={{ marginRight: '0.30rem', opacity: '0' }} className={`${isActive && 'opacity-1'}`} />
        {/* fields for start date */}
        <input id={START_DATE_INPUT_ID} className="date_picker_filed" type="text" readOnly value={startDate} />
        to
        {/* fields for end date */}
        <input id={END_DATE_INPUT_ID} readOnly className="date_picker_filed" type="text" value={endDate} />
      </div>

      {/* container box for showing the calendar component */}
      <div style={{ position: 'relative' }}>
        <div ref={refStartDate} style={{ position: 'relative' }} className="d-block">
          {/**
            show calendar for start_date based on the click and state of end date
            if first clicked start date and select a date it will hide the calendar for 
            start_date and show the calendar for end date once end date selected it will call the API
            and get the data.
          */}
          {showCalendarStartDate ? (
            <div className="start-date-picker-calendar">
              <DatePicker
                locale="en"
                endDate={new Date()}
                minDate={new Date('01/01/2018')}
                maxDate={maxStartDate}
                showYearDropdown
                yearDropdownItemNumber={6}
                selected={startDate ? new Date(startDate) : new Date()}
                dateFormat="yyyy-MM-dd"
                dateFormatCalendar="MMMM"
                onChange={(date: any) => {
                  date = format(date, 'yyyy-MM-dd').split('T')[0];
                  setStartDate(date);
                  if (!endDate) {
                    setShowCalendarEndDate(true);
                  } else {
                    setMenuHeight(false);
                    setDates(date, endDate);
                  }
                  setShowCalendarStartDate(false);
                  if (setDateFilter && setDatesObj) {
                    setDatesObj({ start_date: date, end_date: endDate });
                    if (date !== '' && endDate !== '') {
                      setDateFilter({
                        label: format(new Date(date), 'MMM dd, yy') + ' to ' + format(new Date(endDate), 'MMM dd, yy'),
                        value: 'custom',
                      });
                    }
                  }
                  if (filteredCampaigns && endDate !== '') {
                    filteredCampaigns(
                      'dates',
                      { label: format(new Date(date), 'MMM dd, yy') + ' to ' + format(new Date(endDate), 'MMM dd, yy'), value: 'custom' },
                      { start_date: date, end_date: endDate },
                    );
                  } else if (filterEmails && endDate !== '') {
                    filterEmails(
                      'dates',
                      { label: format(new Date(date), 'MMM dd, yy') + ' to ' + format(new Date(endDate), 'MMM dd, yy'), value: 'custom' },
                      { start_date: date, end_date: endDate },
                    );
                  } else if (props && props.onChange && endDate !== '' && props.isSmartSource) {
                    props.onChange({ value: { value: { start_date: date, end_date: endDate }, type: 'custom' } });
                  }
                }}
                inline
                onClickOutside={(date: any) => {
                  setShowCalendarStartDate(false);
                  setMenuHeight(false);
                }}
                onInputClick={() => {
                  console.log('inputclick');
                }}
              />
            </div>
          ) : null}
          {showCalendarEndDate ? (
            <div className="end-date-picker-calendar">
              <DatePicker
                locale="en"
                selected={endDate ? new Date(endDate) : new Date()}
                endDate={new Date()}
                dateFormat="yyyy-MM-dd"
                dateFormatCalendar="MMMM"
                minDate={startDate ? new Date(startDate) : new Date('01/01/2018')}
                maxDate={new Date()}
                showYearDropdown
                yearDropdownItemNumber={6}
                onChange={(date: any) => {
                  date = format(date, 'yyyy-MM-dd').split('T')[0];
                  setEndDate(date);
                  if (!startDate) {
                    setShowCalendarStartDate(true);
                  } else {
                    setMenuHeight(false);
                    setDates(startDate, date);
                  }
                  setMaxStartDate(new Date(date));
                  setShowCalendarEndDate(false);
                  if (setDateFilter && setDatesObj) {
                    setDatesObj({ start_date: startDate, end_date: date });
                    if (date !== '' && startDate !== '') {
                      setDateFilter({
                        label: format(new Date(startDate), 'MMM dd, yy') + ' to ' + format(new Date(date), 'MMM dd, yy'),
                        value: 'custom',
                      });
                    }
                  }
                  if (filteredCampaigns && startDate !== '') {
                    filteredCampaigns(
                      'dates',
                      { label: format(new Date(startDate), 'MMM dd, yy') + ' to ' + format(new Date(date), 'MMM dd, yy'), value: 'custom' },
                      { start_date: startDate, end_date: date },
                    );
                  } else if (filterEmails && startDate !== '') {
                    filterEmails(
                      'dates',
                      { label: format(new Date(startDate), 'MMM dd, yy') + ' to ' + format(new Date(date), 'MMM dd, yy'), value: 'custom' },
                      { start_date: startDate, end_date: date },
                    );
                  } else if (props && props.onChange && startDate !== '' && props.isSmartSource) {
                    props.onChange({
                      value: { value: { start_date: startDate, end_date: date }, type: 'custom' },
                    });
                  }
                }}
                inline
                onClickOutside={(date: any) => {
                  setShowCalendarEndDate(false);
                  setMenuHeight(false);
                }}
              />
            </div>
          ) : null}
        </div>
      </div>
    </>
  );

  // change handler for Dates
  const onChangeHere = (value: any) => {
    if (value.value !== 'custom') {
      setStartDate('');
      setEndDate('');
      setMaxStartDate(new Date());
      if (select) {
        if (props && props.onChange) props.onChange(value);
        setStartDate('');
        setEndDate('');
        if (setDateFilter && setDatesObj && filteredCampaigns) {
          setDateFilter(value);
          setDatesObj({ startDate: '', endDate: '' });
          filteredCampaigns('dates', value);
        } else if (setDateFilter && setDatesObj && filterEmails) {
          setDateFilter(value);
          setDatesObj({ startDate: '', endDate: '' });
          filterEmails('dates', value);
        } else if (setDateFilter && props.isSmartSource) {
          setDateFilter(value);
        }
        setShowMenuIsOpen(false);
        setMenuHeight(false);
        select.blur();
      }
    }
  };

  function handleOutsideClickStart(event: any) {
    if (refStartDate?.current && !refStartDate.current?.contains(event.target)) {
      if (event.target.id === START_DATE_INPUT_ID) {
        setShowCalendarEndDate(false);
        setShowCalendarStartDate(true);
        if (!menuHeight) setMenuHeight(true);
      } else if (event.target.id === END_DATE_INPUT_ID) {
        setShowCalendarStartDate(false);
        setShowCalendarEndDate(true);
        if (!menuHeight) setMenuHeight(true);
      } else {
        if (showCalendarStartDate) setShowCalendarStartDate(false);
        if (showCalendarEndDate) setShowCalendarEndDate(false);
        if (menuHeight) setMenuHeight(false);
      }
    }
  }

  // onBlur handler for the dates menu
  const onBlurSelect = () => (el: any) => {
    if (props.value?.value === 'custom' && startDate.length > 0 && endDate.length > 0) {
      setShowMenuIsOpen(false);
      select.blur();
    } else if (props.value?.value === 'custom') {
      setShowMenuIsOpen(false);
      if (showCalendarEndDate) setShowCalendarEndDate(false);
      if (showCalendarStartDate) setShowCalendarStartDate(false);
      if (menuHeight) setMenuHeight(false);
      select.blur();
    }
    if (showCalendarEndDate) setShowCalendarEndDate(false);
    if (showCalendarStartDate) setShowCalendarStartDate(false);
    if (menuHeight) setMenuHeight(false);
  };

  // Adding click event listener
  useEffect(() => {
    // Function for click event
    if (refStartDate) {
      document.addEventListener('click', handleOutsideClickStart);
    }
    return () => {
      document.removeEventListener('click', handleOutsideClickStart);
    };
  }, [refStartDate]);

  useEffect(() => {
    if (props.value?.value === 'custom') {
      if (datesObj?.start_date !== '' && datesObj?.start_date !== startDate) {
        setStartDate(datesObj?.start_date);
      }
      if (datesObj?.end_date !== '' && datesObj?.end_date !== endDate) {
        setEndDate(datesObj?.end_date);
      }
    }
  }, [datesObj, dateFilter]);

  useEffect(() => {
    if (props.value?.value === ASSET_TYPE.CUSTOM && appliedStartDate && appliedEndDate) {
      setStartDate(appliedStartDate);
      setEndDate(appliedEndDate);
    }
    // appliedStartDate, appliedEndDate in the dependency array are used to run the affect on tab switch.
  }, [appliedStartDate, appliedEndDate]);

  return (
    <Wrapper className={`${props.className === undefined ? '' : props.className}`} data-id="date-select-filter" onSelectFocus={onFocus}>
      <RSelect
        {...props}
        isDisabled={props.disabled}
        id={'date-select-filter-wrapper-' + id}
        defaultValue={props.defaultValue ?? (props.options ? props.options[0] : null)}
        components={{
          Control,
          DropdownIndicator,
          SingleValue,
          ValueContainer,
          Option,
        }}
        isSearchable={false}
        name="emoji"
        styles={styles}
        ref={(ref) => {
          select = ref;
        }}
        // conditionally add props to react-select based on if customDatePicker
        {...(props.customDatePicker
          ? {
              customOption: <CustomOption isActive={props.value?.value === ASSET_TYPE.CUSTOM} />,
              onChange: onChangeHere,
              ...(showMenuIsOpen ? { menuIsOpen: true } : {}),
              onFocus: () => {
                setShowMenuIsOpen(!showMenuIsOpen);
              },
              onBlur: () => {
                // Comment to make dropdown visible all the time
                onBlurSelect();
                setShowMenuIsOpen(!showMenuIsOpen);
              },
            }
          : props.onChange
          ? { onChange: props.onChange }
          : '')}
        closeMenuOnSelect={props.value?.value === ASSET_TYPE.CUSTOM && startDate?.length === 0 && endDate?.length === 0 ? false : true}
        // menuIsOpen={true}
        onMenuOpen={() => {
          setOnFocus(true);
        }}
      />
    </Wrapper>
  );
};

export default SelectWithIcon;

const EmojiWrapper = styled.span<{ heightWidth?: number }>`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Wrapper = styled.div<{ onSelectFocus?: boolean }>`
  .css-vqtttq-ValueContainer {
    margin: 2px 8px 2px 0px;
  }
  .react-datepicker__month-read-view--down-arrow,
  .react-datepicker__year-read-view--down-arrow {
    top: 3px;
  }
  .react-datepicker__navigation--next {
    top: 10px;
    right: 50px;
  }
  .react-datepicker__navigation--previous {
    top: 10px;
    left: 50px;
  }
  .react-datepicker__year-dropdown {
    top: 50px;
  }
  .react-datepicker__year-read-view {
    visibility: visible !important;
  }
  .react-datepicker__navigation--years-previous,
  .react-datepicker__navigation--years-upcoming {
    display: none;
  }
  .if-else-dropdown,
  .wait-dropdown {
    .qa-control {
      :nth-child(2) {
        > div {
          @media all and (max-width: 1920px) {
            position: unset;
          }
          .css-c3py3x-singleValue {
            top: ${(props) => (props.onSelectFocus ? '0px' : '')};
            transform: ${(props) => (props.onSelectFocus ? 'translateY(12px)' : '')};
            @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
              transform: ${(props) => (props.onSelectFocus ? 'translateY(0px)' : '')};
            }
          }
        }
      }
    }
  }
  > div {
    z-index: 6;
    > div {
      @media all and (max-width: 1439px) {
        min-height: 30px;
        border-radius: 6px;
        height: auto;
        width: auto;
      }
      > div > div > span > div {
        @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
          font-size: 13px;
        }
      }
    }
  }
  .react-datepicker-wrapper {
    display: inline-block;
    padding: 0;
    border: 0;
    width: 40%;
  }

  .react-datepicker__input-container {
    position: relative;
    display: inline-block;
    width: 100%;
    input {
      width: 100%;
    }
  }
  .date-pickers {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .date_picker_filed {
    width: 40%;
    border: 1px solid grey;
    border-radius: 5px;
  }

  .start-date-picker-calendar {
    position: relative;
    display: flex;
    justify-content: center;
    transform: scale(0.9);
  }

  .end-date-picker-calendar {
    position: relative;
    display: flex;
    justify-content: center;
    transform: scale(0.9);
  }
  .opacity-1 {
    opacity: 1 !important;
  }
  .deactivate-span {
    > span {
      display: flex;
      align-items: center;
      justify-content: space-between;
    }
  }
  .deactivated-tag {
    width: 71px;
    margin-left: 0.75rem;
    padding-top: 3px;
    background: #f1f1f1;
    border: 0.8px solid #dadada;
    border-radius: 6px;

    font-family: 'Roboto';
    font-style: normal;
    font-weight: 500;
    font-size: 11px;
    line-height: 13px;
    text-align: center;
    letter-spacing: -0.4px;
    color: #a5a5a5;
  }
  .checked-wrap {
    .ant-typography {
      display: inline-block;
      max-width: 110px;
      color: #dadada;
      margin-bottom: -6px;
    }
  }
`;
